import { useCallback, useEffect, useState } from "react";
import { DeleteTwoTone, EditTwoTone, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Space,
  Upload,
  message,
  Row,
  Col,
  Typography,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Switch,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import {
  getData,
  postData,
  deleteData,
  putData,
} from "../../../apiServices/common";
import {
  OB_SECTION_LIST,
  OB_SECTION_ADD,
  OB_SECTION_EDIT,
  OB_SECTION_DELETE,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  OB_SECTION_CREATE_PERMISSION,
  OB_SECTION_DELETE_PERMISSION,
  OB_SECTION_EDIT_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const OBSectionConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [ObSectionForm] = Form.useForm();
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllObSection = useCallback(
    async (filterValues) => {
      const { per_page, name } = filterValues || {};

      setLoading(true);
      setFetchError(null);

      const query = `${OB_SECTION_LIST}?page=${currentPage}`;
      const bodyData = {
        ...(per_page && { per_page: per_page }),
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting Color data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllObSection(filterData);
  }, [refresh, filterData, getAllObSection]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    ObSectionForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedObSection = async (id) => {
    setLoading(true);
    const response = await deleteData(`${OB_SECTION_DELETE}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("OB Section Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedObSection = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status,
    };

    const response = await putData(
      `${OB_SECTION_EDIT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("OB Section Edited Successfully");
      refetch();
      ObSectionForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewOBSection = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status,
    };

    const response = await postData(OB_SECTION_ADD, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("OB Section Created Successfully");
      refetch();
      ObSectionForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);
    ObSectionForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      description: record?.description,
      status: record?.status ? true : false,
    });
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: ["name"],
      key: "name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "status",
      width: "10%",
      render: (_, record) => {
        return <div>{record?.status ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedObSection(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([OB_SECTION_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([OB_SECTION_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([OB_SECTION_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([OB_SECTION_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>OB Section Configuration</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllObSection}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllObSection(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={
          <span style={{ fontSize: "14px" }}>
            {isEdit ? "Edit OB Section" : "Create OB Section"}
          </span>
        }
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={ObSectionForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedObSection : createNewOBSection}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: "Name Required",
                  },
                ]}
              >
                <Input size="small" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                placeholder="Description"
                label="Description"
                rules={[
                  {
                    required: false,
                    message: "Description Required",
                  },
                ]}
              >
                <Input size="small" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  size="small"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
                size="small"
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
                size="small"
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default OBSectionConfig;
